import * as p from '@clack/prompts'
import path from 'node:path'
import type { AddCommandOptions } from '@cli-types/core-engine/commands.js'
import { detectPackageManager } from '@cli-utils/process.js'
import { writeConfigFile } from '@core-engine/config/serialize/write-config-file.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace/resolve-admin-namespace.js'
import { resolveConfigOrExit } from '@next-adapter/config/resolve-config-or-exit.js'
import { runPostGenerate } from '@next-adapter/generators/post-generate/run-post-generate.js'
import { scanConflictPaths } from '@next-adapter/generators/post-generate/scan-conflict-paths.js'
import { syncProjectCliDependency } from '@next-adapter/init/scaffolders/dependencies/sync-project-cli-dependency.js'
import {
  formatUnknownIntegrationMessage,
  isIntegrationId
} from '@next-adapter/integration-registry.js'
import { installIntegrations } from '@next-adapter/integration-runtime/install-integrations.js'
import {
  formatUnknownPresetMessage,
  getPresetDefinition,
  isPresetId
} from '@next-adapter/preset-registry.js'
import { installPresets } from '@next-adapter/preset-runtime/install-presets.js'
import { printAddNextSteps } from './print-add-next-steps.js'

export async function runAddCommand(items: string[], options: AddCommandOptions): Promise<void> {
  const installIntegrationsMode = Boolean(options.integration)
  const presetIds = items.filter(isPresetId)
  const integrationIds = items.filter(isIntegrationId)

  if (!installIntegrationsMode && integrationIds.length > 0) {
    p.log.error(
      `Integration IDs require --integration. Run \`betterstart add --integration ${integrationIds.join(' ')}\`.`
    )
    process.exit(1)
  }

  if (installIntegrationsMode && presetIds.length > 0) {
    p.log.error(`Preset IDs cannot be installed with --integration: ${presetIds.join(', ')}`)
    process.exit(1)
  }

  const invalidItems = installIntegrationsMode
    ? items.filter((integrationId) => !isIntegrationId(integrationId))
    : items.filter((presetId) => !isPresetId(presetId))
  if (invalidItems.length > 0) {
    p.log.error(
      installIntegrationsMode
        ? formatUnknownIntegrationMessage(invalidItems)
        : formatUnknownPresetMessage(invalidItems)
    )
    process.exit(1)
  }

  const cwd = options.cwd ? path.resolve(options.cwd) : process.cwd()
  const config = await resolveConfigOrExit(cwd)
  const adminRoutePath = resolveAdminNamespace(config.frameworkConfig.next.namespace).routePath
  const pm = detectPackageManager(cwd)
  const cliSyncResult = await syncProjectCliDependency(cwd, pm)

  if (cliSyncResult && !cliSyncResult.success) {
    p.log.error(cliSyncResult.error ?? 'Failed to sync betterstart-cli')
    process.exit(1)
  }

  if (installIntegrationsMode) {
    const result = await installIntegrations({
      cwd,
      config,
      pm,
      integrationIds,
      interactive: !options.yes,
      includeLinter: false
    })

    writeConfigFile(cwd, result.config)

    if (result.warnings.length > 0) {
      p.note(result.warnings.join('\n'), 'Warnings')
    }

    if (
      result.installed.length === 0 &&
      result.activated.length === 0 &&
      result.skipped.length > 0
    ) {
      p.outro(`No changes made. Already installed: ${result.skipped.join(', ')}`)
      return
    }

    const conflictPaths = scanConflictPaths(cwd)
    const postGenerateResult = await runPostGenerate(
      cwd,
      result.installed[0] ?? result.activated[0] ?? 'admin',
      {
        skipMigration: true,
        skipMigrationMessage: 'no schema changes',
        hasConflicts: conflictPaths.length > 0,
        conflictPaths,
        showNextSteps: false
      }
    )

    if (conflictPaths.length === 0) {
      printAddNextSteps('integration', false, postGenerateResult, adminRoutePath)
    }

    const messages = [
      result.installed.length > 0
        ? `Installed integration${result.installed.length === 1 ? '' : 's'}: ${result.installed.join(', ')}`
        : null,
      result.activated.length > 0
        ? `Activated integration${result.activated.length === 1 ? '' : 's'}: ${result.activated.join(', ')}`
        : null
    ].filter(Boolean)

    p.outro(messages.join('\n'))
    return
  }

  const invalidPresets = items.filter((presetId) => !isPresetId(presetId))
  if (invalidPresets.length > 0) {
    p.log.error(formatUnknownPresetMessage(invalidPresets))
    process.exit(1)
  }

  const result = await installPresets({
    cwd,
    config,
    pm,
    presetIds,
    interactive: !options.yes,
    includeLinter: false
  })

  writeConfigFile(cwd, result.config)

  if (result.installed.length === 0 && result.skipped.length > 0) {
    p.outro(`No changes made. Already installed: ${result.skipped.join(', ')}`)
    return
  }

  if (result.warnings.length > 0) {
    p.note(result.warnings.join('\n'), 'Warnings')
  }

  const installedSchemaNames = result.installed.flatMap((presetId) =>
    getPresetDefinition(presetId).schemaFiles.map((schemaFile) => schemaFile.replace(/\.json$/, ''))
  )
  const hasSchemaChanges = installedSchemaNames.length > 0
  const conflictPaths = scanConflictPaths(cwd)
  const postGenerateResult = await runPostGenerate(cwd, installedSchemaNames[0] ?? 'admin', {
    skipMigration: !hasSchemaChanges || Boolean(options.skipMigration),
    skipMigrationMessage: hasSchemaChanges ? '--skip-migration' : 'no schema changes',
    hasConflicts: conflictPaths.length > 0,
    conflictPaths,
    showNextSteps: false
  })

  if (conflictPaths.length === 0) {
    printAddNextSteps('preset', hasSchemaChanges, postGenerateResult, adminRoutePath)
  }

  p.outro(
    `Installed preset${result.installed.length === 1 ? '' : 's'}: ${result.installed.join(', ')}`
  )
}
